import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { useMergeRefs } from '@dni-react-hooks/use-merge-refs'
import { usePrevious } from '@dni-react-hooks/use-previous'
import { cssSupport } from '@dni-utils/css-support'
import * as stylex from '@stylexjs/stylex'
import { type TransitionEventHandler, useCallback, useEffect, useRef, type ComponentPropsWithoutRef, type ForwardedRef, type PropsWithChildren } from 'react'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'

const dynamicStyles = stylex.create({
  duration: (duration: number) => ({
    transitionDuration: `${duration}ms`,
  }),
  collapsePeek: (peekHeight: number) => ({
    height: peekHeight,
  }),
})

const verticalStyles = stylex.create({
  root: {
    overflow: 'hidden',
    position: 'relative',
    transitionProperty: 'height',
  },
  collapseIn: {
    height: 'auto',
  },
  collapseOut: {
    height: 0,
  },
  gradientMask: {
    backgroundImage: 'linear-gradient(to top, white, transparent)',
    pointerEvents: 'none',
    position: 'absolute',
    bottom: 0,
    height: 20,
    left: 0,
    right: 0,
  },
})

const horizontalStyles = stylex.create({
  root: {
    transitionProperty: 'width',
  },
  collapseIn: {
    width: 'auto',
  },
  collapseOut: {
    width: 0,
  },
  gradientMask: {
    backgroundImage: 'linear-gradient(to right, white, transparent)',
    pointerEvents: 'none',
    position: 'absolute',
    bottom: 0,
    height: 20,
    left: 0,
    right: 0,
    top: 0,
  },
})

const _baseStyles = stylex.create({
  root: {
    overflow: 'hidden',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
})

export interface CollapseProps extends ComponentPropsWithoutRef<'div'> {
  /** 根元素的 ref */
  rootRef?: ForwardedRef<HTMLDivElement>
  /** 控制展開（true）或收合（false） */
  in?: boolean
  /** 自訂根元素的 StyleX 樣式 */
  rootStyle?: stylex.StyleXStyles
  /** 覆蓋內部子樣式的 xStyles */
  xStyles?: Partial<XStyles<keyof typeof _baseStyles>>
  /**
   * 過渡動畫持續時間（毫秒）
   * @default 225
   */
  duration?: number
  /**
   * 收合時是否顯示部分內容
   * @default false
   */
  showPart?: boolean
  /**
   * 收合時可見的高度（px），僅在 showPart 為 true 時有效
   * @default 60
   */
  peekHeight?: number
  /**
   * 收合方向
   * @default 'vertical'
   */
  orientation?: 'horizontal' | 'vertical'
}

const supportsInterpolateSize = cssSupport('interpolate-size:allow-keywords')

/**
 * Collapse 是展開/收合過渡動畫元件，支援垂直與水平方向。
 * 可選擇性地在收合時保留部分內容可見（showPart + peekHeight）。
 */
export const Collapse = (props: PropsWithChildren<CollapseProps>) => {
  const {
    rootRef: rootRefProp,
    in: inProp,
    rootStyle,
    xStyles,
    children,
    duration = 225,
    showPart = false,
    peekHeight = 60,
    orientation = 'vertical',
    onTransitionEnd,
    ...rest
  } = props
  const baseStyles = useXStyles(_baseStyles, xStyles)
  const rootRef = useRef<HTMLDivElement>(null)
  const mergeRef = useMergeRefs(rootRefProp, rootRef)
  const prevInProp = usePrevious(inProp)

  const handleCollapseInTransitionEnd: TransitionEventHandler<HTMLDivElement> = useCallback((event) => {
    onTransitionEnd?.(event)
    if (!rootRef.current || !inProp || supportsInterpolateSize) return
    rootRef.current.style.height = 'auto'
  }, [inProp, onTransitionEnd])

  const getContentHeight = useCallbackEvent(() => {
    if (!rootRef?.current) return 0
    rootRef.current.style.height = 'auto'
    const height = rootRef.current.clientHeight
    rootRef.current.style.height = ''
    return height
  })

  const updateHeight = useCallbackEvent((contentHeight: number, nextHeight: number) => {
    const node = rootRef.current
    if (!node) return
    node.style.height = `${contentHeight}px`
    /**
     * 瀏覽器在一個渲染週期會批次處理，使用 requestAnimationFrame 讓瀏覽器在下一個渲染週期更新 height
     * 才能正確觸發 transition 動畫
     */
    requestAnimationFrame(() => {
      node.style.height = `${nextHeight}px`
    })
  })

  /**
   * For Browser that doesn't support interpolate-size:allow-keywords
   */
  useEffect(() => {
    if (supportsInterpolateSize || prevInProp === undefined || prevInProp === inProp) return
    const handleCollapseIn = () => {
      const height = getContentHeight()
      updateHeight(showPart ? peekHeight : 0, height)
    }
    const handleCollapseOut = () => {
      const height = getContentHeight()
      updateHeight(height, showPart ? peekHeight : 0)
    }
    if (inProp) {
      handleCollapseIn()
    }
    else {
      handleCollapseOut()
    }
  }, [getContentHeight, inProp, prevInProp, updateHeight, showPart, peekHeight])

  const orientationStyles = orientation === 'vertical' ? verticalStyles : horizontalStyles

  const getHeightStyle = useCallback(() => {
    if (inProp) {
      return orientationStyles.collapseIn
    }
    if (showPart) {
      return dynamicStyles.collapsePeek(peekHeight)
    }
    return orientationStyles.collapseOut
  }, [inProp, showPart, peekHeight, orientationStyles])

  return (
    <div
      ref={mergeRef}
      {...stylex.props([
        baseStyles.root,
        orientationStyles.root,
        dynamicStyles.duration(duration),
        getHeightStyle(),
        inProp ? orientationStyles.collapseIn : orientationStyles.collapseOut,
        rootStyle,
      ])}
      onTransitionEnd={handleCollapseInTransitionEnd}
      {...rest}
    >
      {children}
      {showPart && (
        <div {...stylex.props(orientationStyles.gradientMask)} />
      )}
    </div>
  )
}
